// Product cards (snippets/card-product.liquid): when slot 2 is a Shopify-hosted video, its still carries
// data-sa-card-video with its MP4 (720p, ~0.8 MB). On a desktop with a mouse, resting on a card for HOVER_DELAY_MS
// fades that video in, muted, over the card's front photo (the slot-2 still is skipped: it is not the video's first
// frame). Leaving the card pauses it and goes back to the start. Nothing loads before a hover, and phones, tablets,
// reduced motion and Save-Data get the plain still. Clarity hears card_video_play once per page.
// Phones and tablets (< 990px): a card with more than one media (.sa-card-gallery) swipes through up to 5 of them,
// one at a time, with the dot pill following; resting on slot 2 plays its video muted in the card (one card at a
// time), any other slide or the card leaving the screen stops it, and a plain tap opens the product. Slides 3–5 load
// when a swipe comes near them, the video only once slot 2 is reached. Clarity hears card_gallery_swipe once per page.
(() => {
  if (window.saCardVideo) return;
  const HOVER_DELAY_MS = 100;
  const DESKTOP = '(hover: hover) and (pointer: fine) and (min-width: 990px)';
  const CARD = '.card-wrapper';
  const STILL = '.media--hover-effect > img[data-sa-card-video]';
  // A card's quick-add popup lives inside the card, so a mouse over the open popup is not a hover on the card.
  const POPUP = 'quick-add-modal, dialog, modal-dialog, .quick-add-modal__content';
  // Videos kept ready for a quick return to a card; older ones are dropped so a long browse does not hold them all.
  const KEEP = 4;

  let current = null;
  let timer = null;
  let reported = false;
  const kept = [];

  const GALLERY_QUERY = '(max-width: 989px)';
  const GALLERY = '.media.sa-card-gallery';

  const calm = () =>
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches && navigator.connection?.saveData !== true;
  const allowed = () => window.matchMedia(DESKTOP).matches && calm();
  const galleryOn = () => window.matchMedia(GALLERY_QUERY).matches;

  // Tells component-card.css that hovering a video card fades the video in over the front photo, skipping the still.
  // sa-card-gallery-on lays the phone gallery out; leaving it (a tablet turned to landscape) goes back to photo 1.
  const mark = () => {
    const root = document.documentElement;
    root.classList.toggle('sa-card-video-on', allowed());
    const wasGallery = root.classList.contains('sa-card-gallery-on');
    root.classList.toggle('sa-card-gallery-on', galleryOn());
    if (wasGallery && !galleryOn()) {
      leave();
      for (const track of document.querySelectorAll(GALLERY)) {
        track.scrollLeft = 0;
        show(track, 0);
      }
    }
  };
  for (const query of [DESKTOP, GALLERY_QUERY, '(prefers-reduced-motion: reduce)']) {
    window.matchMedia(query).addEventListener?.('change', mark);
  }

  function drop(video) {
    video.pause();
    video.removeAttribute('src');
    video.load();
    video.remove();
  }

  function keep(video) {
    const at = kept.indexOf(video);
    if (at >= 0) kept.splice(at, 1);
    kept.push(video);
    while (kept.length > KEEP) drop(kept.shift());
  }

  function videoFor(still) {
    let video = still.parentElement.querySelector(':scope > video.sa-card-video');
    if (video) {
      keep(video);
      return video;
    }
    video = document.createElement('video');
    video.className = 'sa-card-video';
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.loop = true;
    video.preload = 'none';
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('aria-hidden', 'true');
    video.tabIndex = -1;
    video.src = still.dataset.saCardVideo;
    video.addEventListener('playing', () => {
      if (video.closest(CARD) !== current) return video.pause();
      video.classList.add('is-playing');
      if (!reported && typeof window.clarity === 'function') {
        reported = true;
        try {
          window.clarity('event', 'card_video_play');
        } catch {}
      }
    });
    // A file that fails keeps the still and is not asked for again on the next hover.
    video.addEventListener('error', () => {
      if (!video.getAttribute('src')) return;
      const at = kept.indexOf(video);
      if (at >= 0) kept.splice(at, 1);
      still.removeAttribute('data-sa-card-video');
      video.remove();
    });
    still.after(video);
    keep(video);
    return video;
  }

  function start(card) {
    const still = card.querySelector(STILL);
    if (!still) return;
    const video = videoFor(still);
    const attempt = video.play();
    if (attempt?.catch) attempt.catch(() => {});
  }

  function stop(card) {
    const video = card.querySelector('video.sa-card-video');
    if (!video) return;
    video.classList.remove('is-playing');
    video.pause();
    if (video.readyState > 0) video.currentTime = 0;
  }

  function enter(card) {
    if (card === current) return;
    leave();
    if (!card.querySelector(STILL) || !allowed()) return;
    current = card;
    timer = setTimeout(() => {
      timer = null;
      if (current === card && !document.hidden) start(card);
    }, HOVER_DELAY_MS);
  }

  function leave() {
    clearTimeout(timer);
    timer = null;
    if (current) stop(current);
    current = null;
  }

  document.addEventListener('pointerover', (event) => {
    if (!allowed() || (event.pointerType && event.pointerType !== 'mouse')) return;
    const card = event.target.closest?.(POPUP) ? null : event.target.closest?.(CARD);
    if (card) enter(card);
    else leave();
  });
  document.addEventListener('pointerout', (event) => {
    if (!current || !allowed() || (event.pointerType && event.pointerType !== 'mouse')) return;
    if (!current.contains(event.relatedTarget)) leave();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) leave();
  });
  window.addEventListener('pagehide', leave);

  // ---- Phone gallery ----
  let swiped = false;
  const tracks = new WeakMap();

  const slideOf = (track) => (track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0);
  const slideImage = (track, index) =>
    index === 0 ? track.querySelector(':scope > img') : track.querySelector(`:scope > [data-sa-slide="${index + 1}"]`);

  function warm(track, index) {
    const image = slideImage(track, index);
    if (!image) return;
    if (image.dataset.srcset) {
      image.srcset = image.dataset.srcset;
      image.removeAttribute('data-srcset');
    }
    if (image.dataset.src) {
      image.src = image.dataset.src;
      image.removeAttribute('data-src');
    }
    if (image.loading === 'lazy') image.loading = 'eager';
  }

  function show(track, index) {
    const dots = track.parentElement.querySelectorAll('.sa-card-dot');
    dots.forEach((dot, at) => dot.classList.toggle('is-active', at === index));
  }

  function settle(track) {
    const state = tracks.get(track);
    const index = slideOf(track);
    if (index === state.index) return;
    state.index = index;
    show(track, index);
    if (state.visible) warm(track, index + 1);
    if (!swiped && index > 0 && typeof window.clarity === 'function') {
      swiped = true;
      try {
        window.clarity('event', 'card_gallery_swipe');
      } catch {}
    }
    const card = track.closest(CARD);
    if (index === 1 && state.visible && calm() && track.querySelector(STILL)) {
      if (current !== card) leave();
      current = card;
      if (!document.hidden) start(card);
    } else if (current === card) {
      leave();
    }
  }

  // Off screen (scrolled past, or the home row moved on) stops the video; coming back on slot 2 plays it again.
  const seen = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const entry of entries) {
          const state = tracks.get(entry.target);
          if (!state) continue;
          state.visible = entry.isIntersecting;
          if (entry.isIntersecting) warm(entry.target, 1);
          state.index = -1;
          if (galleryOn()) settle(entry.target);
        }
      }, {threshold: 0.5})
    : null;

  function track(element) {
    if (tracks.has(element)) return;
    tracks.set(element, {index: 0, visible: !seen});
    seen?.observe(element);
    let frame = 0;
    element.addEventListener(
      'scroll',
      () => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          settle(element);
        });
      },
      {passive: true}
    );
    // A finger on the photo is the cue to fetch the next photo before it slides in.
    element.addEventListener('touchstart', () => warm(element, slideOf(element) + 1), {passive: true});
  }

  function scan(root = document) {
    for (const element of root.querySelectorAll?.(GALLERY) || []) track(element);
  }

  // The gallery sits above the card link, so a tap (not a swipe: the browser sends no click after one) follows it.
  document.addEventListener('click', (event) => {
    if (!galleryOn()) return;
    const inner = event.target.closest?.('.card__inner');
    if (!inner || !inner.querySelector(GALLERY)) return;
    const link = inner.closest(CARD)?.querySelector('a[id^="CardLink-"]') || inner.closest(CARD)?.querySelector('.card__heading a[href]');
    if (!link) return;
    event.preventDefault();
    link.click();
  });

  // Cards added later (filters, infinite scroll, recommendations) get the gallery too.
  if ('MutationObserver' in window) {
    new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.(GALLERY)) track(node);
          else scan(node);
        }
      }
    }).observe(document.documentElement, {childList: true, subtree: true});
  }

  mark();
  scan();

  window.saCardVideo = {HOVER_DELAY_MS};
})();
